import * as React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components';
import { Style } from '../styles';
import { CurrentUser, PublicLocale, MimeTypes, WidgetCardType, WidgetSpace, Widget as WidgetType, WidgetCard as CardType, ExternalLinksWidgetCard, HelpCenter, NewsCenter, PostWidgetCard } from '../types';
import Utils from '../utilities/Utils';
import { WidgetCardsController, WidgetsController } from '../controllers';
import { Helmet } from 'react-helmet';
import TopNavigation from '../components/Navigation/TopNavigation';
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount';
import PageLoader from '../components/Page/PageLoader';
import FullPageContent from '../components/Page/FullPageContent';
import { AppState } from '../store';
import { showConfirmModal, showCustomComponentModal, showWidgetModal } from '../store/modals/actions';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps, Switch, Route, Redirect } from 'react-router-dom';
import RouteHelper from '../helpers/RouteHelper';
import ERoute from '../ERoute';
import TopSubnavigation from '../components/Navigation/TopSubnavigation';
import TabItem from '../components/Navigation/TabItem';
import AccordionContainer from '../components/Accordion/AccordionContainer';
import Accordion from '../components/Accordion/Accordion';
import { codepen, CopyBlock } from 'react-code-blocks';
import Panel from '../components/Panel/Panel';
import Title from '../components/Settings/Title';
import MobilityHelper from '../helpers/MobilityHelper';
import CheckboxInput from '../components/Form/CheckboxInput';
import Alert from '../components/Alert/Alert';
import SwatchPicker from '../components/Swatch/SwatchPicker';
import HelpCenterHelper from '../helpers/HelpCenterHelper';
import Tooltip from '../components/Tooltips/Tooltip';
import AttachmentUploader from '../components/Form/AttachmentUploader';
import { WIDGET_HEADER_FILE_SIZE, WIDGET_LOGO_FILE_SIZE } from '../Constants';
import { Blob } from '@rails/activestorage';
import PowerSelect from '../components/Form/PowerSelect';
import ResizeObserver from '../components/ResizeObserver/ResizeObserver';
import WidgetSpaces from '../components/Widget/WidgetSpaces';
import WidgetCard from '../components/Widget/WidgetCard';
import { DragDropContext, Droppable, DropResult, ResponderProvided } from '@hello-pangea/dnd';
import DroppableHelper from '../helpers/DroppableHelper';
import ExternalLink from '../components/Widget/Forms/ExternalLink';
import ResourceCreatablePowerSelect from '../components/Form/ResourceCreatablePowerSelect';
import Post from '../components/Widget/Forms/Post';
import WidgetOutboundMessageTable from './Widget/WidgetOutboundMessageTable';
import WidgetOutboundMessage from './Widget/WidgetOutboundMessage';
import WidgetTourTable from './Widget/WidgetTourTable';
import WidgetTour from './Widget/WidgetTour';

const Container = styled.div`
 position: relative;
	display: flex;
	flex-direction: row;
	height: 100%;
	position: relative;
  overflow: hidden;

  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    flex-direction: column-reverse;
  }
`

const PageLoaderContainer = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const Content = styled.div`
  padding: ${Style.spacing.x3};
  flex: 1;
  overflow: auto;
  border-right: 1px solid ${Style.color.border};

  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    border-right: none;
    height: calc(50% - 106px);
  }
`

const Preview = styled.div`
  position: relative;
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: center;

  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    height: calc(50% - 106px);
    border-bottom: 1px solid ${Style.color.border};
  }

`

const PreviewWrapper = styled.div`
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  border-radius: 0 0 8px 8px;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    border-radius: 0 0 8px 8px;
  }
`

const SwatchPickerContainer = styled.div`
  width: 38px;
  height: 38px;
  border: 1px solid ${Style.color.border};
  border-radius: ${Style.variables.baseBorderRadius};
  overflow: hidden;
`

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
`

const CardsDescription = styled.div`
  margin-bottom: 20px;

  h4 {
    font-size: 14px;
    font-weight: 700;
    color: #1a1a1a;
  }

  p {
    font-size: 14px;
    line-height: 20px;
  }

`

const Spacer = styled.div`
  margin: 24px 0;
  height: 1px;
  background: ${Style.color.border};
`

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  showWidgetModal: typeof showWidgetModal
  showConfirmModal: typeof showConfirmModal
  showCustomComponentModal: typeof showCustomComponentModal
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation & RouteComponentProps<{ id: string }>

interface IState {
  widget: WidgetType | null,
  didInitialLoad: boolean,
  selectedLocale: PublicLocale,
}

class Widget extends React.Component<IProps, IState> {
  private preview = React.createRef<HTMLIFrameElement>();

  constructor(props) {
    super(props);

    this.state = {
      widget: null,
      didInitialLoad: false,
      selectedLocale: PublicLocale.EN,
    }

    this.fetchWidgetView = this.fetchWidgetView.bind(this)
    this.updateWidget = this.updateWidget.bind(this)
    this.updateWidgetDebounced = Utils.debounce(this.updateWidget, 250, false)
    this.reloadWidget = this.reloadWidget.bind(this)
    this.onWidgetSpacesChange = this.onWidgetSpacesChange.bind(this)
    this.onWelcomeMessageChange = this.onWelcomeMessageChange.bind(this)
    this.onAddCardClick = this.onAddCardClick.bind(this)
    this.onSettingsClick = this.onSettingsClick.bind(this)
    this.onRemoveCardClick = this.onRemoveCardClick.bind(this)
    this.createCard = this.createCard.bind(this)
    this.updateCard = this.updateCard.bind(this)
    this.onWidgetCardFormSubmit = this.onWidgetCardFormSubmit.bind(this)
    this.onShowMessengerLauncherChange = this.onShowMessengerLauncherChange.bind(this)
    this.onActionColorChange = this.onActionColorChange.bind(this)
    this.onBackgroundColorChange = this.onBackgroundColorChange.bind(this)
    this.onLogoLightChange = this.onLogoLightChange.bind(this)
    this.onLogoDarkChange = this.onLogoDarkChange.bind(this)
    this.onShowAvatarsChange = this.onShowAvatarsChange.bind(this)
    this.onHeaderImageChange = this.onHeaderImageChange.bind(this)
    this.onHeaderHeightChange = this.onHeaderHeightChange.bind(this)
    this.onHeaderBackgroundFadeChange = this.onHeaderBackgroundFadeChange.bind(this)
    this.onStartConversationButtonTextChange = this.onStartConversationButtonTextChange.bind(this)
    this.onShowReplyExpectationChange = this.onShowReplyExpectationChange.bind(this)
    this.onUserWelcomeMessageChange = this.onUserWelcomeMessageChange.bind(this)
    this.onSpecialNoticeChange = this.onSpecialNoticeChange.bind(this)
    this.onSpecialNoticeTextChange = this.onSpecialNoticeTextChange.bind(this)
    this.onDefaultLocaleChange = this.onDefaultLocaleChange.bind(this)
    this.onSupportedLocalesChange = this.onSupportedLocalesChange.bind(this)
    this.onAllowedDomainsChange = this.onAllowedDomainsChange.bind(this)
    this.onAudioEnabledChange = this.onAudioEnabledChange.bind(this)
    this.onDragCardEnd = this.onDragCardEnd.bind(this)
    this.onHelpCenterChange = this.onHelpCenterChange.bind(this)
    this.onNewsCenterChange = this.onNewsCenterChange.bind(this)
  }

  componentDidMount(): void {
    this.fetchWidgetView().catch(console.error)
  }

  async fetchWidgetView() {
    const { match: { params: { id } } } = this.props

    try {
      const { widget, } = await WidgetsController.getView(id)

      this.setState({
        widget: widget,
        selectedLocale: widget.locale as PublicLocale,
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  async updateWidget(widget: WidgetType) {
    try {
      const response = await WidgetsController.update(widget)

      if (!response.errors) {
        this.setState({
          widget: response
        }, this.reloadWidget)
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  updateWidgetDebounced(widget: WidgetType) {
    this.updateWidget(widget).catch(console.error)
  }

  reloadWidget() {
    if (this.preview.current) {
      this.preview.current.contentWindow?.postMessage(
        { type: 'widget_reload' },
        '*'
      )
    }
  }

  onWidgetSpacesChange(spaces: WidgetSpace[]) {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      spaces: spaces,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onWelcomeMessageChange(locale: PublicLocale, welcomeMessage: string) {
    const { widget } = this.state

    const updatedTranslations = MobilityHelper.updateTranslation(locale, widget.translations, 'welcome_message', welcomeMessage)
    const updatedWidget: WidgetType = {
      ...widget,
      translations: updatedTranslations
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onAddCardClick(card: CardType) {
    const { t } = this.props
    const { widget } = this.state

    try {
      const newCard = {
        ...card,
        id: null,
        position: widget.cards.length + HelpCenterHelper.POSITION_STEP_INDEX
      }

      switch (card.type) {
        // Immediately create cards that don't need any settings
        case WidgetCardType.NEW_CONVERSATION:
        case WidgetCardType.HELP_CENTER:
        case WidgetCardType.NEWS:
          this.createCard(newCard).catch(console.error)
          break
        case WidgetCardType.POST:
          const emptyPostWidgetCard: PostWidgetCard = {
            ...newCard,
            type: WidgetCardType.POST,
            translations: {}
          }
          requestAnimationFrame(() => {
            this.props.showCustomComponentModal(({
              title: t('Widget::Configure post'),
              component: (
                <Post
                  widget={widget}
                  card={emptyPostWidgetCard as PostWidgetCard}
                  onSubmit={this.onWidgetCardFormSubmit}
                />
              )
            }))
          })
          break
        // Show external link settings before creating
        case WidgetCardType.EXTERNAL_LINK:
          const emptyExternalLinkCard: ExternalLinksWidgetCard = {
            ...newCard,
            type: WidgetCardType.EXTERNAL_LINK,
            external_links: [
              { label: '', url: '' }
            ]
          }
          requestAnimationFrame(() => {
            this.props.showCustomComponentModal(({
              title: t('Widget::Configure external links'),
              component: (
                <ExternalLink
                  widgetId={widget.id}
                  card={emptyExternalLinkCard as ExternalLinksWidgetCard}
                  onSubmit={this.onWidgetCardFormSubmit}
                />
              )
            }))
          })
          break
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  onHelpCenterChange(helpCenterId?: string, helpCenter?: HelpCenter) {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      help_center_id: helpCenterId,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onNewsCenterChange(newsCenterId?: string, newsCenter?: NewsCenter) {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      news_center_id: newsCenterId,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  async createCard(card: CardType) {
    const { widget } = this.state
    try {
      const response = await WidgetCardsController.create(widget.id, card)

      this.setState({
        widget: {
          ...widget,
          cards: [...widget.cards, response]
        }
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  async updateCard(card: CardType) {
    const { widget } = this.state

    try {
      const response = await WidgetCardsController.update(widget.id, card)

      this.setState({
        widget: {
          ...widget,
          cards: widget.cards.map(c => {
            if (c.id === response.id) return response
            return c
          })
        }
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  onSettingsClick(card: CardType) {
    const { widget } = this.state
    const { t } = this.props
    switch (card.type) {
      // Immediately create cards that don't need any settings
      case WidgetCardType.POST:
        requestAnimationFrame(() => {
          this.props.showCustomComponentModal(({
            title: t('Widget::Configure post'),
            component: (
              <Post
                widget={widget}
                card={card}
                onSubmit={this.onWidgetCardFormSubmit}
              />
            )
          }))
        })
        break
      // Show external link settings before creating
      case WidgetCardType.EXTERNAL_LINK:
        requestAnimationFrame(() => {
          this.props.showCustomComponentModal(({
            title: t('Widget::Configure external links'),
            component: (
              <ExternalLink
                widgetId={widget.id}
                card={card}
                onSubmit={this.onWidgetCardFormSubmit}
              />
            )
          }))
        })
        break
    }
  }

  async onRemoveCardClick(card: CardType) {
    const { widget } = this.state
    try {
      this.setState({
        widget: {
          ...widget,
          cards: widget.cards.filter(c => c.id !== card.id)
        }
      })
      const response = await WidgetCardsController.delete(widget.id, card.id)
      this.fetchWidgetView().catch(console.error)
    } catch (ex) {
      console.error(ex)
    }
  }

  onWidgetCardFormSubmit(card: CardType) {
    const { widget } = this.state

    const cardIndex = widget.cards.findIndex(c => c.id === card.id)

    if (cardIndex === -1) {
      this.setState({
        widget: {
          ...widget,
          cards: [
            ...widget.cards,
            card
          ]
        }
      })
    } else {
      widget.cards[cardIndex] = card

      this.setState({ widget: { ...widget } })
    }
  }

  onShowMessengerLauncherChange(show: boolean) {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      show_launcher: show,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onActionColorChange(color: string) {
    const { widget } = this.state
    const updatedWidget: WidgetType = {
      ...widget,
      action_color: color,
    }
    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onBackgroundColorChange(color: string) {
    const { widget } = this.state
    const updatedWidget: WidgetType = {
      ...widget,
      background_color: color,
    }
    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onLogoLightChange(blob: Blob | null) {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      logo_light: blob?.signed_id || null,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onLogoDarkChange(blob: Blob | null) {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      logo_dark: blob?.signed_id || null,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onShowAvatarsChange() {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      show_avatars: !widget.show_avatars,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onHeaderImageChange(blob: Blob | null) {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      header_image: blob?.signed_id || null,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onHeaderHeightChange(e) {
    const { widget } = this.state
    const updatedWidget: WidgetType = {
      ...widget,
      header_height: parseInt(e.target.value, 10) || 0,
    }
    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onHeaderBackgroundFadeChange() {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      header_background_fade: !widget.header_background_fade,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onStartConversationButtonTextChange(locale: PublicLocale, startConversationButton: string) {
    const { widget } = this.state

    const updatedTranslations = MobilityHelper.updateTranslation(locale, widget.translations, 'start_conversation_button_text', startConversationButton)
    const updatedWidget: WidgetType = {
      ...widget,
      translations: updatedTranslations
    }


    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onShowReplyExpectationChange() {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      show_reply_expectation: !widget.show_reply_expectation,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onUserWelcomeMessageChange(locale: PublicLocale, userWelcomeMessage: string) {
    const { widget } = this.state

    const updatedTranslations = MobilityHelper.updateTranslation(locale, widget.translations, 'user_welcome_message', userWelcomeMessage)
    const updatedWidget: WidgetType = {
      ...widget,
      translations: updatedTranslations
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onSpecialNoticeChange() {
    const { widget } = this.state

    const updatedSpecialNoticeEnabled = !widget.special_notice_enabled
    let updatedWidget: WidgetType = {
      ...widget,
      special_notice_enabled: updatedSpecialNoticeEnabled,
    }

    if (!updatedSpecialNoticeEnabled) {
      let updatedTranslations = { ...widget.translations }
      Object.keys(PublicLocale).forEach((locale) => {
        updatedTranslations = MobilityHelper.updateTranslation(locale as PublicLocale, updatedTranslations, 'special_notice_text', '')
      })

      updatedWidget = {
        ...updatedWidget,
        translations: updatedTranslations
      }
    }


    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onSpecialNoticeTextChange(locale: PublicLocale, specialNoticeText: string) {
    const { widget } = this.state

    const updatedTranslations = MobilityHelper.updateTranslation(locale, widget.translations, 'special_notice_text', specialNoticeText)
    const updatedWidget: WidgetType = {
      ...widget,
      translations: updatedTranslations
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onDefaultLocaleChange(option) {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      locale: option.value,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidget(updatedWidget)
    })
  }

  onSupportedLocalesChange(options) {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      supported_locales: options ? options.map(option => option.value) : [],
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidget(updatedWidget)
    })
  }

  onAllowedDomainsChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const { widget } = this.state

    const allowedDomains = e.target.value
      .split(/[,\n]/)
      .map(domain => domain.trim())
      .filter(domain => domain.length > 0)

    const updatedWidget: WidgetType = {
      ...widget,
      allowed_domains: allowedDomains,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onAudioEnabledChange() {
    const { widget } = this.state

    const updatedWidget: WidgetType = {
      ...widget,
      audio_enabled: !widget.audio_enabled,
    }

    this.setState({ widget: updatedWidget }, () => {
      this.updateWidgetDebounced(updatedWidget)
    })
  }

  onDragCardEnd(result: DropResult, provided: ResponderProvided) {
    if (!result.destination) return;

    const source = result.source;
    const destination = result.destination;

    // did not move anywhere - can bail early
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const { widget } = this.state;


    if (source.droppableId === 'cards') {
      const cards = [...widget.cards];
      const movedCard = widget.cards[source.index];
      const movedCardId = movedCard.id;

      const newCardPosition = DroppableHelper.getNewPositionBasedOnDropResult(cards, result)
      const cardIndex = widget.cards.findIndex(card => card.id === movedCardId)

      if (cardIndex !== -1) {
        cards[cardIndex].position = newCardPosition

        // Get updated boardlist
        const updatedCard: CardType = cards[cardIndex]

        WidgetCardsController.update(widget.id, updatedCard).catch(console.error)

        this.setState({
          widget: {
            ...widget,
            cards: [...cards].sort((a, b) => a.position - b.position)
          }
        })
      }

    }
  }


  renderContent() {
    const { t } = this.props
    const { widget } = this.state

    const supportedLocales = widget.supported_locales
    // Filter out card types that are already added and cannot be added multiple times
    const addedCardTypes = widget.cards
      .map(card => card.type)
      .filter(type => [
        WidgetCardType.NEW_CONVERSATION,
        WidgetCardType.HELP_CENTER,
        WidgetCardType.NEWS
      ].includes(type))
    const availableWidgetCards: CardType[] = [
      { id: 'new-conversation', type: WidgetCardType.NEW_CONVERSATION },
      { id: 'help-center', type: WidgetCardType.HELP_CENTER },
      { id: 'news', type: WidgetCardType.NEWS },
      {
        id: 'post',
        type: WidgetCardType.POST,
        translations: {
          [PublicLocale.EN]: {
            cover_image_url: 'https://picsum.photos/700/400',
            title: t('Widget::Example post', { lng: PublicLocale.EN }),
            summary: t('Widget::This is an example post. You can use posts to share updates, news or articles with your customers.', { lng: PublicLocale.EN }),
            url: 'https://example.com'
          },
          [PublicLocale.NL]: {
            cover_image_url: 'https://picsum.photos/700/400',
            title: t('Widget::Example post', { lng: PublicLocale.NL }),
            summary: t('Widget::This is an example post. You can use posts to share updates, news or articles with your customers.', { lng: PublicLocale.NL }),
            url: 'https://example.com'
          },
          [PublicLocale.FR]: {
            cover_image_url: 'https://picsum.photos/700/400',
            title: t('Widget::Example post', { lng: PublicLocale.FR }),
            summary: t('Widget::This is an example post. You can use posts to share updates, news or articles with your customers.', { lng: PublicLocale.FR }),
            url: 'https://example.com'
          }
        },
      },
      { id: 'external-links', type: WidgetCardType.EXTERNAL_LINK, external_links: [{ label: t('Widget::External link'), url: 'https://example.com' }] }
    ].filter(card => !addedCardTypes.includes(card.type))

    return (
      <AccordionContainer>
        <Accordion title={t('WidgetContent::Spaces')}>
          <WidgetSpaces
            spaces={widget.spaces}
            onChange={this.onWidgetSpacesChange}
          />
        </Accordion>

        <Accordion title={t('WidgetContent::Set your welcome message')}>
          <p style={{ marginBottom: 8 }}>{t('Widget::Say hi to your customers when they open the Messenger.')}</p>

          {widget?.supported_locales?.map((locale) => {
            const translations = widget.translations

            return (
              <div key={locale} className='form-item'>
                <label>{HelpCenterHelper.getLocaleTranslation(locale)}</label>
                <input
                  type='text'
                  value={translations[locale]?.welcome_message || ''}
                  onChange={(e) => this.onWelcomeMessageChange(locale, e.target.value)}
                  placeholder={t('Widget::Say hello')}
                />
              </div>
            )
          })}

          <Spacer />

          {Object
            .keys(PublicLocale)
            .filter(locale => !widget.supported_locales.includes(PublicLocale[locale]))
            .map((locale, index) => {
              const translations = widget.translations
              const localeValue = PublicLocale[locale]

              return (
                <div key={locale} className='form-item'>
                  <label>{HelpCenterHelper.getLocaleTranslation(PublicLocale[locale])}</label>
                  <input
                    type='text'
                    value={translations[localeValue]?.welcome_message || ''}
                    onChange={(e) => this.onWelcomeMessageChange(localeValue, e.target.value)}
                    placeholder={t('Widget::Say hello')}
                  />
                </div>
              )
            })}
        </Accordion>

        <Accordion title={t('WidgetContent::Customize Home with apps')}>
          <DragDropContext onDragEnd={this.onDragCardEnd} >
            <Droppable droppableId='cards' direction='vertical' type='cards'>
              {(droppableProvided, droppableSnapshot) => {
                return (
                  <Cards {...droppableProvided.droppableProps} ref={droppableProvided.innerRef} >
                    {widget?.cards?.map((card, index) => {
                      const hasSettings = [WidgetCardType.POST, WidgetCardType.EXTERNAL_LINK].includes(card.type)
                      return (
                        <WidgetCard
                          key={card.id}
                          index={index}
                          mode='edit'
                          widget={widget}
                          locale={widget.locale}
                          card={card}
                          onAddCardClick={this.onAddCardClick}
                          onSettingsClick={hasSettings ? this.onSettingsClick : null}
                          onRemoveCardClick={this.onRemoveCardClick}
                          isDragDisabled={false}
                        />
                      )
                    })}

                    {droppableProvided.placeholder}
                  </Cards>
                )
              }}
            </Droppable>
          </DragDropContext>
          <CardsDescription>
            <h4>{t('Widget::Add apps to your Messenger')}</h4>
            <p>{t('Widget::Integrate your marketing and sales tools to identify, engage, and convert website visitors.')}</p>
          </CardsDescription>

          <DragDropContext onDragEnd={this.onDragCardEnd} >
            <Droppable droppableId='new-cards' direction='vertical' type='cards'>
              {(droppableProvided, droppableSnapshot) => {
                return (
                  <Cards {...droppableProvided.droppableProps} ref={droppableProvided.innerRef} >
                    {availableWidgetCards.map((widgetCard, index) => {
                      return (
                        <WidgetCard
                          key={widgetCard.id}
                          index={index}
                          mode='add'
                          widget={widget}
                          locale={widget.locale}
                          card={widgetCard}
                          onAddCardClick={this.onAddCardClick}
                          isDragDisabled={true}
                        />
                      )
                    })}
                    {droppableProvided.placeholder}
                  </Cards>
                )
              }}
            </Droppable>
          </DragDropContext>
        </Accordion>

        <Accordion title={t('WidgetContent::Help center')}>
          <div className='form-item'>
            <label>{t('Widget::Configure help center')}</label>
            <ResourceCreatablePowerSelect
              type='help_center'
              value={widget.help_center_id}
              onChange={this.onHelpCenterChange}
              isClearable={true}
            />
          </div>
        </Accordion>

        <Accordion title={t('WidgetContent::News center')}>
          <div className='form-item'>
            <label>{t('Widget::Configure news center')}</label>
            <ResourceCreatablePowerSelect
              type='news_center'
              value={widget.news_center_id}
              onChange={this.onNewsCenterChange}
              isClearable={true}
            />
          </div>
        </Accordion>

        <Accordion title={t('WidgetContent::Show the messenger launcher')}>
          <div className='form-item'>
            <div style={{ marginBottom: 8 }}>
              <Alert
                type='warning'
                text={t('Widget::If you\'re using hide_default_launcher on your bizzeySettings object, it will override the Messenger launcher visibility setting.')}
              />
            </div>
            <div style={{ width: 'fit-content' }}>
              <CheckboxInput
                checked={widget.show_launcher}
                onChange={this.onShowMessengerLauncherChange}
                label={t('Widget::Show messenger launcher')}
              />
            </div>
          </div>
        </Accordion>
      </AccordionContainer >
    )
  }

  renderStyles() {
    const { t } = this.props
    const { widget } = this.state

    return (
      <AccordionContainer>
        <Accordion title={t('WidgetContent::Widget colors')}>
          <div className='form-item'>
            <label>{t('Widget::Action color')}</label>
            <p style={{ marginBottom: 8 }}>{t('Widget::Used in buttons, links and other elements. Color will not update for icons uploaded in JPEG/PNG/GIF format.')}</p>
            <SwatchPickerContainer>
              <SwatchPicker
                color={widget.action_color}
                onChange={this.onActionColorChange}
              />
            </SwatchPickerContainer>
          </div>

          <div className='form-item'>
            <label>{t('Widget::Body background color')}</label>
            <p style={{ marginBottom: 8 }}>{t('Widget::Applies to the background of all pages')}</p>
            <SwatchPickerContainer>
              <SwatchPicker
                color={widget.background_color}
                onChange={this.onBackgroundColorChange}
              />
            </SwatchPickerContainer>
          </div>
        </Accordion>

        <Accordion title={t('WidgetContent::Widget home')}>
          <div className='form-item'>
            <label>
              {t('Widget::Logo light')}
              <Tooltip
                content={t('Widget::Add a logo that will be used on dark backgrounds. Available formats are PNG, JPG or GIF. Your logo may be resized to fit the widget.')}
                containerStyle={{ marginLeft: 8 }}
              />
            </label>
            <AttachmentUploader
              url={widget.logo_light_url}
              onSubmit={this.onLogoLightChange}
              maxSize={WIDGET_LOGO_FILE_SIZE}
              accept={[
                MimeTypes.PNG,
                MimeTypes.SVG,
                MimeTypes.JPEG,
                MimeTypes.JPG,
                MimeTypes.GIF
              ]}
              light
            />
          </div>

          <div className='form-item'>
            <label>
              {t('Widget::Logo dark')}
              <Tooltip
                content={t('Widget::Add a logo that will be used on light backgrounds. Available formats are PNG, JPG or GIF. Your logo may resized to fit the widget.')}
                containerStyle={{ marginLeft: 8 }}
              />
            </label>
            <AttachmentUploader
              url={widget.logo_dark_url}
              onSubmit={this.onLogoDarkChange}
              maxSize={WIDGET_LOGO_FILE_SIZE}
              accept={[
                MimeTypes.PNG,
                MimeTypes.SVG,
                MimeTypes.JPEG,
                MimeTypes.JPG,
                MimeTypes.GIF
              ]}
            />
          </div>

          <div className='form-item'>
            <label>{t('Widget::Avatars')}</label>
            <p style={{ marginBottom: 8 }}>{t('Widget::Avatars make your messenger more personal')}</p>

            <div style={{ width: 'fit-content' }}>
              <CheckboxInput
                checked={widget.show_avatars}
                onChange={this.onShowAvatarsChange}
                label={t('Widget::Show avatars')}
              />
            </div>
          </div>

          <div className='form-item'>
            <label>{t('Widget::Header image')}</label>
            <AttachmentUploader
              url={widget.header_image_url}
              onSubmit={this.onHeaderImageChange}
              maxSize={WIDGET_HEADER_FILE_SIZE}
              accept={[
                MimeTypes.PNG,
                MimeTypes.JPEG,
                MimeTypes.JPG,
                MimeTypes.WEBP
              ]}
            />
          </div>

          <div className='form-item'>
            <label>{t('Widget::Header height')}</label>
            <input
              type='number'
              value={widget.header_height}
              onChange={this.onHeaderHeightChange}
              style={{ maxWidth: 100 }}
            />
          </div>

          <div className='form-item'>
            <label>{t('Widget::Header background')}</label>
            <p style={{ marginBottom: 8 }}>{t('Widget::Fade background to white. Not suggested if using a GIF as the header image')}</p>
            <div style={{ width: 'fit-content' }}>
              <CheckboxInput
                checked={widget.header_background_fade}
                onChange={this.onHeaderBackgroundFadeChange}
                label={t('Widget::Fade background')}
              />
            </div>
          </div>
        </Accordion>
      </AccordionContainer>
    )
  }

  renderConversations() {
    const { t } = this.props
    const { widget } = this.state

    return (
      <AccordionContainer>
        <Accordion title={t('WidgetContent::Start conversation button text')}>
          {widget?.supported_locales?.map((locale) => {
            const translations = widget.translations
            const localeValue = locale

            return (
              <div key={locale} className='form-item'>
                <label>{HelpCenterHelper.getLocaleTranslation(localeValue)}</label>
                <input
                  type='text'
                  value={translations[localeValue]?.start_conversation_button_text || ''}
                  onChange={(e) => this.onStartConversationButtonTextChange(localeValue, e.currentTarget.value)}
                  placeholder={t('Widget::Send us a message')}
                />
              </div>
            )
          })}

          <Spacer />

          {Object
            .keys(PublicLocale)
            .filter(locale => !widget.supported_locales.includes(PublicLocale[locale]))
            .map((locale, index) => {
              const translations = widget.translations
              const localeValue = PublicLocale[locale]

              return (
                <div key={locale} className='form-item'>
                  <label>{HelpCenterHelper.getLocaleTranslation(localeValue)}</label>
                  <input
                    type='text'
                    value={translations[localeValue]?.start_conversation_button_text || ''}
                    onChange={(e) => this.onStartConversationButtonTextChange(localeValue, e.currentTarget.value)}
                    placeholder={t('Widget::Send us a message')}
                  />
                </div>
              )
            })}
        </Accordion>

        <Accordion title={t('WidgetContent::Reply expectations')}>
          <p style={{ marginBottom: 8 }}>{t('Widget::Show reply times during office hours and indicate when the team will be back online outside office hours')}</p>
          <div style={{ width: 'fit-content' }}>
            <CheckboxInput
              checked={widget.show_reply_expectation}
              onChange={this.onShowReplyExpectationChange}
              label={t('Widget::Show reply times')}
            />
          </div>
        </Accordion>

        <Accordion title={t('WidgetContent::Team introduction')}>
          {widget?.supported_locales?.map((locale) => {
            const translations = widget.translations
            const localeValue = locale

            return (
              <div key={locale} className='form-item'>
                <label>{HelpCenterHelper.getLocaleTranslation(localeValue)}</label>
                <input
                  type='text'
                  value={translations[localeValue]?.user_welcome_message || ''}
                  onChange={(e) => this.onUserWelcomeMessageChange(localeValue, e.target.value)}
                  placeholder={t('Widget::Tell people how you can help.')}
                />
              </div>
            )
          })}

          <Spacer />

          {Object
            .keys(PublicLocale)
            .filter(locale => !widget.supported_locales.includes(PublicLocale[locale]))
            .map((locale, index) => {
              const translations = widget.translations
              const localeValue = PublicLocale[locale]

              return (
                <div key={locale} className='form-item'>
                  <label>{HelpCenterHelper.getLocaleTranslation(PublicLocale[locale])}</label>
                  <input
                    type='text'
                    value={translations[localeValue]?.user_welcome_message || ''}
                    onChange={(e) => this.onUserWelcomeMessageChange(localeValue, e.target.value)}
                    placeholder={t('Widget::Tell people how you can help.')}
                  />
                </div>
              )
            })}
        </Accordion>

        <Accordion title={t('WidgetContent::Special notice')}>
          <p style={{ marginBottom: 8 }}>{t('Widget::Write a notice to all your visitors and customers.')}</p>
          <div className='form-item'>
            <div style={{ width: 'fit-content' }}>
              <CheckboxInput
                checked={widget.special_notice_enabled}
                onChange={this.onSpecialNoticeChange}
                label={t('Widget::Enabled')}
              />
            </div>
          </div>

          {widget.special_notice_enabled && <>
            {widget.supported_locales.map((locale, index) => {
              const translations = widget.translations
              const localeValue = locale

              return (
                <div key={locale} className='form-item'>
                  <label>{HelpCenterHelper.getLocaleTranslation(localeValue)}</label>
                  <input
                    type='text'
                    value={translations[localeValue]?.special_notice_text || ''}
                    onChange={(e) => this.onSpecialNoticeTextChange(localeValue, e.target.value)}
                    placeholder={t('Widget::Send us a message')}
                  />
                </div>
              )
            })}
            <Spacer />
            {Object
              .keys(PublicLocale)
              .filter(locale => !widget.supported_locales.includes(PublicLocale[locale]))
              .map((locale, index) => {
                const translations = widget.translations
                const localeValue = PublicLocale[locale]

                return (
                  <div key={locale} className='form-item'>
                    <label>{HelpCenterHelper.getLocaleTranslation(PublicLocale[locale])}</label>
                    <input
                      type='text'
                      value={translations[localeValue]?.special_notice_text || ''}
                      onChange={(e) => this.onSpecialNoticeTextChange(localeValue, e.target.value)}
                      placeholder={t('Widget::Send us a message')}
                    />
                  </div>
                )
              })}
          </>}
        </Accordion>
      </AccordionContainer>
    )
  }

  renderGeneral() {
    const { t } = this.props
    const { widget } = this.state

    const availableLocaleOptions = HelpCenterHelper.getAvailableLocaleOptions()
    const defaultLocaleOption = availableLocaleOptions.find(option => option.value === widget.locale)

    const additionalLocaleOptions = availableLocaleOptions.filter(option => option.value !== widget.locale)
    const selectedLocaleOptions = additionalLocaleOptions.filter(option => widget.supported_locales.includes(option.value as PublicLocale))

    return (
      <AccordionContainer>
        <Accordion title={t('WidgetContent::Choose supported languages')}>
          <div className='form-item'>
            <label>{t('Widget::Default language')}</label>
            <PowerSelect
              options={availableLocaleOptions}
              value={defaultLocaleOption}
              onChange={this.onDefaultLocaleChange}
              placeholder={t('Widget::Select default language')}
            />
          </div>

          <div className='form-item'>
            <label>{t('Widget::Additional languages')}</label>
            <PowerSelect
              options={additionalLocaleOptions}
              value={selectedLocaleOptions}
              onChange={this.onSupportedLocalesChange}
              placeholder={t('Widget::Select additional languages')}
              // @ts-ignore
              isMulti={true}
            />
          </div>
        </Accordion>

        <Accordion title={t('WidgetContent::Keep your widget secure')}>
          <p style={{ marginBottom: 8 }}>{t('Widget::List your trusted domains and subdomains, separated by commas. To mark all your subdomains as trusted, use an asterisk as a placeholder like this: *.example.com. If you leave this field blank, your widget can be added to any domain.')}</p>
          <textarea
            className='form-item'
            defaultValue={widget.allowed_domains.join(', ')}
            onBlur={this.onAllowedDomainsChange}
            placeholder={t('Widget::Enter your trusted domains, separated by commas')}
          />
        </Accordion>

        <Accordion title={t('WidgetContent::Other preferences')}>
          <div className='form-item'>
            <label>{t('Widget::Sound')}</label>
            <p style={{ marginBottom: 8 }}>{t('Widget::Play a sound for incoming and outgoing messages')}</p>

            <div style={{ width: 'fit-content' }}>
              <CheckboxInput
                checked={widget.audio_enabled}
                onChange={this.onAudioEnabledChange}
                label={t('Widget::Enabled')}
              />
            </div>
          </div>
        </Accordion>
      </AccordionContainer>
    )
  }

  renderTours() {
    const { match: { params: { id } } } = this.props

    return (
      <WidgetTourTable
        widgetId={id}
      />
    )
  }

  renderTour() {
    return (
      <WidgetTour />
    )
  }

  renderOutboundMessage() {
    const { match: { params: { id } } } = this.props

    return (
      <WidgetOutboundMessage />
    )
  }

  renderOutbound() {
    const { match: { params: { id } } } = this.props

    return (
      <WidgetOutboundMessageTable
        widgetId={id}
      />
    )
  }

  renderInstall() {
    const { t, match: { params: { id: widgetId } } } = this.props
    const { widget } = this.state

    return (
      <div className='form-item'>
        <Title>{t('WidgetInstall::Install')}</Title>
        <Panel>
          <div className='form-item'>
            <p>{t('WidgetInstall::Copy and paste this code snippet before the </body> tag on every page you want the messenger to appear.')}</p>
          </div>

          <div className='form-item'>
            <CopyBlock
              text={`<!-- Start Bizzey widget settings -->
  <script>
  window.bizzeySettings = {
    api_base: '${widget.api_base_url}',
    app_id: '${widgetId}', // IMPORTANT: Replace "app.id" with the variable you use to capture the app's ID
    user_id:  '<USER_ID>', // IMPORTANT: Replace "user.id" with the variable you use to capture the user's ID
    name: '<USER_NAME>', // IMPORTANT: Replace "user.name" with the variable you use to capture the user's name
    email:  '<EMAIL>', // IMPORTANT: Replace "user.email" with the variable you use to capture the user's email address
    created_at: '<CREATED AT>', // IMPORTANT: Replace "user.createdAt" with the variable you use to capture the user's sign-up date
  };
  </script>
  <!-- End Bizzey widget settings -->

  <!-- Start of async Bizzey Widget Code -->
  <script>
    (function() { var w = window; var ic = w.Bizzey; if (typeof ic === "function") { ic('reattach_activator'); ic('update', w.bizzeySettings); } else { var d = document; var i = function() { i.c(arguments); }; i.q = []; i.c = function(args) { i.q.push(args); }; w.Bizzey = i; var l = function() { var s = d.createElement('script'); s.type = 'text/javascript'; s.async = true; s.src = '${widget.script_url}'; var x = d.getElementsByTagName('script')[0]; x.parentNode.insertBefore(s, x); }; if (document.readyState === 'complete') { l(); } else if (w.attachEvent) { w.attachEvent('onload', l); } else { w.addEventListener('load', l, false); } } })();
  </script>
  <!-- End of async Bizzey Widget Code -->
  
  `}
              language='html'
              showLineNumbers
              theme={codepen}
              codeBlock
            />
          </div>
        </Panel>
      </div>
    )
  }

  render() {
    const { t, location, match: { params: { id }, url } } = this.props
    const { didInitialLoad, widget } = this.state

    const showPreview = !location.pathname.startsWith(`${url}/outbound`) && !location.pathname.startsWith(`${url}/tours`)

    return (
      <>
        <Helmet>
          <title>{t('Widget::{{__appName}} | Widget')}</title>
        </Helmet>

        <TopNavigation
          icon='messenger-conversations-active'
          title={t('Widget::Widget')}
        />

        <ScrollToTopOnMount />

        {!didInitialLoad && <PageLoaderContainer><PageLoader /></PageLoaderContainer>}

        {didInitialLoad && <FullPageContent>
          <TopSubnavigation>
            <TabItem
              active={location.pathname.startsWith(`${url}/content`)}
              onClick={() => this.props.history.push(`${url}/content`)}
            >
              {t('Widget::Content')}
            </TabItem>
            <TabItem
              active={location.pathname.startsWith(`${url}/styles`)}
              onClick={() => this.props.history.push(`${url}/styles`)}
            >
              {t('Widget::Styles')}
            </TabItem>
            <TabItem
              active={location.pathname.startsWith(`${url}/conversations`)}
              onClick={() => this.props.history.push(`${url}/conversations`)}
            >
              {t('Widget::Conversations')}
            </TabItem>
            <TabItem
              active={location.pathname.startsWith(`${url}/general`)}
              onClick={() => this.props.history.push(`${url}/general`)}
            >
              {t('Widget::General')}
            </TabItem>
            <TabItem
              active={location.pathname.startsWith(`${url}/tours`)}
              onClick={() => this.props.history.push(`${url}/tours`)}
            >
              {t('Widget::Tours')}
            </TabItem>
            <TabItem
              active={location.pathname.startsWith(`${url}/outbound`)}
              onClick={() => this.props.history.push(`${url}/outbound`)}
            >
              {t('Widget::Outbound')}
            </TabItem>
            <TabItem
              active={location.pathname.startsWith(`${url}/install`)}
              onClick={() => this.props.history.push(`${url}/install`)}
            >
              {t('Widget::Install')}
            </TabItem>
          </TopSubnavigation>
          <Container>
            <Content>
              <Switch>
                <Route path={ERoute.PATH_WIDGET_CONTENT} render={() => this.renderContent()} />
                <Route path={ERoute.PATH_WIDGET_STYLES} render={() => this.renderStyles()} />
                <Route path={ERoute.PATH_WIDGET_CONVERSATIONS} render={() => this.renderConversations()} />
                <Route path={ERoute.PATH_WIDGET_GENERAL} render={() => this.renderGeneral()} />
                <Route path={ERoute.PATH_WIDGET_TOUR} render={() => this.renderTour()} />
                <Route path={ERoute.PATH_WIDGET_TOURS} render={() => this.renderTours()} />
                <Route path={ERoute.PATH_WIDGET_OUTBOUND_MESSAGE} render={() => this.renderOutboundMessage()} />
                <Route path={ERoute.PATH_WIDGET_OUTBOUND} render={() => this.renderOutbound()} />
                <Route path={ERoute.PATH_WIDGET_INSTALL} render={() => this.renderInstall()} />
                <Redirect to={RouteHelper.process(ERoute.PATH_WIDGET_CONTENT, { id })} />
              </Switch>
            </Content>

            {showPreview && <Preview>
              <ResizeObserver>
                {() => (
                  <PreviewWrapper>
                    <iframe
                      ref={this.preview}
                      src={widget.preview_url}
                    />
                  </PreviewWrapper>
                )}
              </ResizeObserver>
            </Preview>}
          </Container>
        </FullPageContent>}
      </>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    },
  } = state

  return {
    currentUser: currentUser,
  }
}

const mapDispatchToProps: IDispatchToProps = {
  showWidgetModal,
  showConfirmModal,
  showCustomComponentModal
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Widget))